import { ImageName } from "@mcleod/images";
import { Button } from "../button/Button";
import { Component, ComponentCreator, Cursor, DesignerInterface, PanelProps } from "../..";
import { ComponentPropDefinition } from "../../base/ComponentProps";
import { DesignableObjectTempState } from "../../base/DesignableObjectTempState";
import { MultiButtonPropDefinitions, MultiButtonProps } from "./MultiButtonProps";
import { Overlay } from "../../page/Overlay";
import { Panel } from "../panel/Panel";

export class MultiButton extends Panel implements MultiButtonProps {
    public primaryButton: Button;
    public secondaryButton: Button;
    private secondaryButtonPreBusyState: DesignableObjectTempState;

    constructor(props?: Partial<MultiButtonProps>) {
        super(props, false);
        this.primaryButton = this.createPrimaryButton();
        this.secondaryButton = this.createSecondaryButton();
        this.add(this.primaryButton);
        this.setProps({ ...this.defaultProps, ...props });
    }

    setProps(props: Partial<MultiButtonProps>) {
        super.setProps(props);
    }

    protected get defaultProps(): Partial<PanelProps> {
        return { borderRadius: 4, padding: 0, maxHeight: 36, margin: 4 };
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.primaryButton, "primaryButton");
        this.setInnerComponentId(this.secondaryButton, "secondaryButton");
    }

    protected createPrimaryButton(): Button {
        return new Button({
            borderWidth: 0,
            fillRow: true,
            borderTopRightRadius: 0,
            padding: 4,
            borderBottomRightRadius: 0,
            borderRadius: 4,
            margin: 0,
            rowBreak: false,
            height: 32,
            id: this.id + "_primary"
        });
    }

    protected createSecondaryButton(): Button {
        const result = new Button({
            caption: "",
            margin: 0,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            paddingRight: 0,
            paddingLeft: 4,
            borderWidth: 0,
            borderLeftColor: "strokePrimary",
            borderLeftWidth: 1,
            rowBreak: false,
            imageWidth: MultiButtonPropDefinitions.getDefinitions().dropdownImageSize.defaultValue,
            imageHeight: MultiButtonPropDefinitions.getDefinitions().dropdownImageSize.defaultValue,
            imageName: ImageName.arrow,
            maxWidth: 32,
            height: 32,
            fillRow: true
        });
        this.setSecondaryButtonAction(result);
        return result;
    }

    protected setSecondaryButtonAction(button: Button): void {
        button.handleDropdownClick = async () => {
            button["._overlay"] = Overlay.showDropdown(
                this,
                this.dropdownItems, null,
                { ...button.dropdownListProps },
                button.dropdownOverlayProps
            ).overlay;
        };
    }

    get busyWhenDataSourceBusy(): boolean {
        return this.primaryButton.busyWhenDataSourceBusy == null ?
            MultiButtonPropDefinitions.getDefinitions().busyWhenDataSourceBusy.defaultValue :
            this.primaryButton.busyWhenDataSourceBusy;
    }

    set busyWhenDataSourceBusy(value: boolean) {
        this.primaryButton.busyWhenDataSourceBusy = value;
    }

    get busy(): boolean {
        return this.primaryButton.busy === true;
    }

    set busy(value: boolean) {
        try {
            if (value === this.busy) {
                return;
            }
            this.primaryButton.busy = value;
            if (value === true) {
                // Disable the overall MultiButton so that the secondary button cannot be clicked
                if (this.secondaryButtonPreBusyState == null) {
                    this.secondaryButtonPreBusyState = new DesignableObjectTempState({
                        enabled: this.secondaryButton.enabled
                    });
                }
                this.secondaryButton.enabled = false;
            }
        } finally {
            if (value !== true && this.secondaryButtonPreBusyState != null) {
                this.secondaryButton.setProps(this.secondaryButtonPreBusyState.originalProps);
                this.secondaryButtonPreBusyState = null;
            }
        }
    }

    get _interactionEnabled(): boolean {
        return super._interactionEnabled;
    }

    set _interactionEnabled(value: boolean) {
        super._interactionEnabled = value;
        this.primaryButton._interactionEnabled = value;
        this.secondaryButton._interactionEnabled = value;
    }

    public getEventTarget(): HTMLElement {
        return this.primaryButton._element;
    }

    get dropdownItems(): ComponentCreator[] {
        return this.secondaryButton.dropdownItems as ComponentCreator[];
    }

    set dropdownItems(value: ComponentCreator[]) {
        this.secondaryButton.dropdownItems = value;
        if (value == null || value.length === 0) {
            this.remove(this.secondaryButton);
            this.primaryButton.borderTopRightRadius = 4;
            this.primaryButton.borderBottomRightRadius = 4;
        } else {
            if (!this.contains(this.secondaryButton)) {
                this.add(this.secondaryButton);
            }
            this.primaryButton.borderTopRightRadius = 0;
            this.primaryButton.borderBottomRightRadius = 0;
        }
    }

    get caption(): string {
        return this.primaryButton.caption;
    }

    set caption(value: string) {
        this.primaryButton.caption = value;
    }

    get hotkey(): string {
        return this.primaryButton.hotkey;
    }

    set hotkey(value: string) {
        this.primaryButton.hotkey = value;
    }

    get id(): string {
        return super.id;
    }

    set id(value: string) {
        super.id = value;
        if (this.primaryButton != null)
            this.primaryButton.id = value + "_primary";
        if (this.secondaryButton != null)
            this.secondaryButton.id = value + "_secondary";
    }

    get imageName(): ImageName {
        return this.primaryButton.imageName;
    }

    set imageName(value: ImageName) {
        this.primaryButton.imageName = value;
    }

    get dropdownImageSize(): number {
        return this.secondaryButton.imageWidth;
    }

    set dropdownImageSize(value: number) {
        this.secondaryButton.imageWidth = value;
        this.secondaryButton.imageHeight = value;
    }

    get showSeparator(): boolean {
        return this.secondaryButton.borderLeftWidth > 0;
    }

    set showSeparator(value: boolean) {
        this.secondaryButton.borderLeftWidth = value ? 1 : 0;
    }

    override _applyEnabled(value: boolean) {
        this.primaryButton.enabled = value;
        if (this.secondaryButton != null) {
            this.secondaryButton.enabled = value;
        }
        value ? this.applyEnabledTheme() : this.applyDisabledTheme();
    }

    protected applyEnabledTheme() {
        this._element.removeAttribute("disabled");
        this.primaryButton._element.removeAttribute("disabled");
        this.secondaryButton._element.removeAttribute("disabled");
        this.primaryButton.style.cursor = this.cursor;
        this.secondaryButton.style.cursor = this.cursor;
        this._element.style.opacity = "1";
    }

    protected applyDisabledTheme() {
        if (this.__designer == null) {
            this._element.setAttribute("disabled", "true");
            this.primaryButton._element.setAttribute("disabled", "true");
            this.secondaryButton._element.setAttribute("disabled", "true");
        }
        this._element.style.opacity = "0.5";
        this.primaryButton.style.cursor = Cursor.DEFAULT;
        this.secondaryButton.style.cursor = Cursor.DEFAULT;
    }

    public get _designer(): DesignerInterface {
        return super._designer;
    }

    public set _designer(value: DesignerInterface) {
        super._designer = value;
        this.primaryButton.setDesigner(value);
        this.secondaryButton.setDesigner(value);
    }

    _serializeNonProps(): string {
        //we don't want to serialize this component like a normal Panel
        //the primary button is controlled the top-level props, so that rendered button shouldn't be serialized
        //like dropdown actions should not be serialized, those are controlled by props as well
        return "";
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (this.defaultProps[prop.name] != null)
            return this.defaultProps[prop.name];
        if (prop.name === "showSeparator")
            return true; //because default borderLeftWidth > 0
        return super.determinePropertyDefaultValue(prop);
    }

    public isNestedDesignerContainer(): boolean {
        return true;
    }

    public override discoverIncludedComponents(): Component[] {
        //return null here so that the components that make up the multi button aren't included
        return null;
    }

    public override set backgroundColor(value: string) {
        this.primaryButton.backgroundColor = value;
        this.secondaryButton.backgroundColor = value;
    }

    public override set color(value: string) {
        this.primaryButton.color = value;
        this.secondaryButton.color = value;
    }
}
